export const formatDuration = (seconds: number): string => {
  if (!seconds) return '0:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const formatViews = (views: number): string => {
  if (!views) return '0';

  if (views >= 1000000000) {
    return `${(views / 1000000000).toFixed(1)}B`;
  }
  if (views >= 1000000) {
    return `${(views / 1000000).toFixed(1)}M`;
  }
  if (views >= 1000) {
    return `${(views / 1000).toFixed(1)}K`;
  }
  return views.toString();
};

import { formatDistanceToNow } from 'date-fns';

export const formatDate = (dateString: string): string => {
  if (!dateString) return 'Unknown Date';
  try {
    const date = new Date(dateString);
    // Check if the date is valid before formatting
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date Error'; // Or handle as appropriate
  }
};

export const formatDateShort = (dateString: string): string => {
  if (!dateString) return 'Unknown';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid';
    }

    // Use local time for consistent calculation (YouTube dates now include timezone info)
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();

    // Handle future dates - show as positive time ago
    if (diffInMs < 0) {
      const absDiffInMs = Math.abs(diffInMs);
      const absDiffInMinutes = Math.floor(absDiffInMs / (1000 * 60));
      const absDiffInHours = Math.floor(absDiffInMs / (1000 * 60 * 60));
      const absDiffInDays = Math.floor(absDiffInMs / (1000 * 60 * 60 * 24));

      if (absDiffInMinutes < 1) {
        return 'Just now';
      } else if (absDiffInMinutes < 60) {
        return `${absDiffInMinutes}m ago`;
      } else if (absDiffInHours < 24) {
        return `${absDiffInHours}h ago`;
      } else if (absDiffInDays < 7) {
        return `${absDiffInDays}d ago`;
      } else if (absDiffInDays < 30) {
        const weeks = Math.floor(absDiffInDays / 7);
        return `${weeks}w ago`;
      } else if (absDiffInDays < 365) {
        const months = Math.floor(absDiffInDays / 30);
        return `${months}mo ago`;
      } else {
        const years = Math.floor(absDiffInDays / 365);
        return `${years}y ago`;
      }
    }

    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks}w ago`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months}mo ago`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return `${years}y ago`;
    }
  } catch (error) {
    console.error('Error formatting short date:', error);
    return 'Error';
  }
};

export const formatLikes = (likes: number): string => {
  if (!likes) return '0';

  if (likes >= 1000000) {
    return `${(likes / 1000000).toFixed(1)}M`;
  }
  if (likes >= 1000) {
    return `${(likes / 1000).toFixed(1)}K`;
  }
  return likes.toString();
};

export const parseGenres = (genres: string | null): string[] => {
  if (!genres) return [];

  // Handle comma-separated genres
  return genres.split(',').map(g => g.trim()).filter(g => g.length > 0);
};

export const parseTags = (tags: string[] | string | null): string[] => {
  if (!tags) return [];

  if (Array.isArray(tags)) return tags;

  // Handle JSON string or comma-separated string
  try {
    return JSON.parse(tags);
  } catch {
    return tags.split(',').map(t => t.trim()).filter(t => t.length > 0);
  }
};

export const getYouTubeUrl = (videoId: string): string => {
  return `https://www.youtube.com/watch?v=${videoId}`;
};

export const getYouTubeThumbnail = (videoId: string, quality: 'default' | 'medium' | 'high' | 'maxres' = 'high'): string => {
  return `https://img.youtube.com/vi/${videoId}/${quality}default.jpg`;
};

// Helper function to extract manhwa title from recap description
export const extractManhwaTitle = (description: string): string => {
  if (!description) return 'Manhwa Recap';

  // Common patterns to extract title from recap descriptions
  const patterns = [
    /^([^-]+)\s*-/,        // "Title - rest"
    /^([^|]+)\s*\|/,       // "Title | rest"
    /^([^:]+)\s*:/,        // "Title: rest"
    /^([^(]+)\s*\(/,       // "Title (rest)"
    /^(.{1,50})\s/         // First 50 chars before space
  ];

  for (const pattern of patterns) {
    const match = description.match(pattern);
    if (match && match[1].trim().length > 3) {
      return match[1].trim();
    }
  }

  // Fallback: first 40 characters
  return description.length > 40 ? description.substring(0, 40) + '...' : description;
};